import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useStore } from '@24i/nxg-sdk-smartott/src/context/ApplicationStore';
import { ApplicationStates, useAppStart } from '@24i/nxg-sdk-smartott/src/context/AppStartContext';
import { resetUserPseudoId, useUserData } from '@24i/nxg-sdk-smartott-shared/src/context/UserData';
import { log } from '@24i/nxg-core-utils/src/logger';
import { useFirebase } from '@24i/nxg-sdk-smartott-shared/src/context/Firebase';
import {
    onAnalytics,
    setUserInfo,
    ANALYTICS_TRIGGERS,
    ANALYTICS_EVENTS,
} from '@24i/nxg-sdk-smartott-shared/src/analytics';
import { useUserFetch } from '@24i/nxg-sdk-smartott-shared/src/hooks/query/user/useUserFetch';
import { useUserProfilesFetch } from '@24i/nxg-sdk-smartott-shared/src/hooks/query/user/useUserProfilesFetch';
import { useFeature } from '@24i/nxg-sdk-smartott-shared/src/context/AppSettingsData';

import { useStartPageNavigation } from '../../context/AppStartContext/hooks';

export interface UseUserSessionProps {
    isLoading: boolean;
    signInUser: (email: string, password: string) => Promise<{ login: boolean; message?: string }>;
    signOutUser: () => Promise<void>;
}

const useUserSession = (): UseUserSessionProps => {
    const { setAppState } = useAppStart();
    const userDataClient = useUserData();
    const { recordError } = useFirebase();
    const { fetch: fetchUser } = useUserFetch();
    const { fetch: fetchUserProfiles } = useUserProfilesFetch();
    const { login } = useStartPageNavigation();
    const { t } = useTranslation();
    const { setUserData, setSelectedUserProfile, setProfilesAndPossiblySelectOne } = useStore();
    const [isLoading, setIsLoading] = useState(false);
    const profilesEnabled = useFeature('accounts')?.select_profiles || false;

    const signInUser = async (
        email: string,
        password: string
    ): Promise<{ login: boolean; message?: string }> => {
        setIsLoading(true);
        try {
            await userDataClient.login(email, password);
            setAppState(ApplicationStates.initialized);
            const user = await fetchUser({ forceRefresh: true });
            if (user) {
                setUserInfo({ userId: user.id as string });
                if (profilesEnabled) {
                    const profiles = await fetchUserProfiles({ forceRefresh: true });
                    setProfilesAndPossiblySelectOne(profiles);
                }
                onAnalytics(ANALYTICS_TRIGGERS.LOGIN, { event: ANALYTICS_EVENTS.LOGGED_IN });
            }

            await resetUserPseudoId();
            setUserData(user);
            await login();
            return { login: true };
        } catch (error: any) {
            recordError(error);
            log(error);
            if (error.error || error instanceof Error) {
                if (error.error === 'BAD_CREDENTIALS') {
                    error.message = t('error.E00.body');
                }
                return { login: false, message: error.message };
            }
            return { login: false, message: JSON.stringify(error) };
        } finally {
            setIsLoading(false);
        }
    };

    const signOut = async () => {
        setIsLoading(true);

        try {
            setUserData(null);
            setSelectedUserProfile(null);
            await resetUserPseudoId();
        } catch (error) {
            log(error);
            recordError(error);
            /* eslint-disable no-alert */
            alert(JSON.stringify(error) || t('error.A00.title'));
        } finally {
            setIsLoading(false);
        }
    };

    return { isLoading, signInUser, signOutUser: signOut };
};

export default useUserSession;
